import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "hi", label: "Hindi" },
  { code: "ta", label: "Tamil" },
  { code: "te", label: "Telugu" },
  { code: "mr", label: "Marathi" },
  { code: "gu", label: "Gujarati" },
  { code: "kn", label: "Kannada" },
  { code: "bn", label: "Bengali" },
  { code: "ml", label: "Malayalam" },
  { code: "pa", label: "Punjabi" },
];

const ALL_TOOLS = [
  { title: "Capital Gain Calculator", icon: "📈", link: "/tools/capital-gain", free: true },
  { title: "TDS Calculator", icon: "🧮", link: "/tools/tds-calculator", free: true },
  { title: "Bank Statement to Excel", icon: "🏦", link: "/tools/bank-statement", free: true },
  { title: "Property Capital Gain", icon: "🏠", link: "/tools/property-gain", free: true },
  { title: "TDS on Overseas Payments", icon: "🌏", link: "/tools/overseas-tds", free: false },
  { title: "Crypto Capital Gain", icon: "🪙", link: "/tools/crypto-gain", free: false },
];

const FREE_TOOLS = ALL_TOOLS.filter(t => t.free);

function ToolsDropdown({ tools, onClose }: { tools: typeof ALL_TOOLS; onClose: () => void }) {
  return (
    <div style={{
      position: "absolute", top: "calc(100% + 8px)", left: 0,
      background: "white", border: "1.5px solid #EAE6E0",
      borderRadius: "14px", padding: "16px",
      minWidth: "520px", zIndex: 300,
      boxShadow: "0 8px 40px rgba(0,0,0,0.15)",
      display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px"
    }}>
      {tools.map((tool, idx) => (
        <Link key={idx} to={tool.link} onClick={onClose}
          style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", borderRadius: "8px", textDecoration: "none", transition: "background 0.15s", background: "transparent" }}
          onMouseEnter={e => { e.currentTarget.style.background = "#F5F2EE"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
          <span style={{ fontSize: "22px", flexShrink: 0 }}>{tool.icon}</span>
          <div>
            <div style={{ fontSize: "13px", fontWeight: 600, color: "#2C2C2C" }}>{tool.title}</div>
            <div style={{ fontSize: "11px", color: tool.free ? "#375623" : "#7F6000", fontWeight: 600 }}>
              {tool.free ? "Free" : "Paid"}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

export default function Navbar() {
  const [user, setUser] = useState<any>(null);
  const [picture, setPicture] = useState(localStorage.getItem("userPicture") || "");
  const [profileOpen, setProfileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [allToolsOpen, setAllToolsOpen] = useState(false);
  const [freeToolsOpen, setFreeToolsOpen] = useState(false);
  const [lang, setLang] = useState("en");
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      fetch("/api/auth/me", { headers: { Authorization: "Bearer " + token } })
        .then(r => r.json())
        .then(data => {
          if (data.email) {
            setUser(data);
            const pic = data.picture || localStorage.getItem("userPicture") || "";
            if (pic) {
              setPicture(pic);
              localStorage.setItem("userPicture", pic);
            }
          }
        })
        .catch(() => localStorage.removeItem("token"));
    }
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setProfileOpen(false);
        setLangOpen(false);
      }
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setAllToolsOpen(false);
        setFreeToolsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
    setProfileOpen(false);
    navigate("/");
  };

  const initials = user ? (user.name || user.email || "U")[0].toUpperCase() : "U";
  const navLink: React.CSSProperties = { color: "#D0CCC8", fontSize: "14px", fontWeight: "500", transition: "color 0.2s", textDecoration: "none", cursor: "pointer", background: "none", border: "none", padding: 0 };

  return (
    <nav style={{ background: "#2C2C2C", borderBottom: "2px solid #C9A84C", position: "sticky", top: 0, zIndex: 100, padding: "0 32px", height: "60px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>

      <Link to="/" style={{ display: "flex", alignItems: "center", gap: "10px", textDecoration: "none" }}>
        <div style={{ background: "#C9A84C", width: "36px", height: "36px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "18px", color: "#2C2C2C", fontStyle: "italic" }}>T</div>
        <span style={{ color: "white", fontWeight: 700, fontSize: "18px" }}>Tax<span style={{ color: "#C9A84C" }}>Thinker</span></span>
      </Link>

      <div style={{ display: "flex", alignItems: "center", gap: "28px", position: "relative" }} ref={navRef}>
        <div style={{ position: "relative" }}>
          <button onClick={() => { setAllToolsOpen(!allToolsOpen); setFreeToolsOpen(false); }}
            style={{ ...navLink, color: allToolsOpen ? "#C9A84C" : "#D0CCC8", display: "flex", alignItems: "center", gap: "4px" }}
            onMouseEnter={e => { e.currentTarget.style.color = "#C9A84C"; }}
            onMouseLeave={e => { if (!allToolsOpen) e.currentTarget.style.color = "#D0CCC8"; }}>
            All Tools <span style={{ fontSize: "9px" }}>▼</span>
          </button>
          {allToolsOpen && <ToolsDropdown tools={ALL_TOOLS} onClose={() => setAllToolsOpen(false)} />}
        </div>

        <div style={{ position: "relative" }}>
          <button onClick={() => { setFreeToolsOpen(!freeToolsOpen); setAllToolsOpen(false); }}
            style={{ ...navLink, color: freeToolsOpen ? "#C9A84C" : "#D0CCC8", display: "flex", alignItems: "center", gap: "4px" }}
            onMouseEnter={e => { e.currentTarget.style.color = "#C9A84C"; }}
            onMouseLeave={e => { if (!freeToolsOpen) e.currentTarget.style.color = "#D0CCC8"; }}>
            Free Tools <span style={{ fontSize: "9px" }}>▼</span>
          </button>
          {freeToolsOpen && <ToolsDropdown tools={FREE_TOOLS} onClose={() => setFreeToolsOpen(false)} />}
        </div>

        <Link to="/pricing"
          style={{ ...navLink, color: "#D0CCC8" } as React.CSSProperties}
          onMouseEnter={e => { e.currentTarget.style.color = "#C9A84C"; }}
          onMouseLeave={e => { e.currentTarget.style.color = "#D0CCC8"; }}>
          Pricing
        </Link>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "14px" }} ref={ref}>

        <button onClick={toggleTheme} title="Toggle dark mode" style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(201,168,76,0.3)", borderRadius: "20px", width: "48px", height: "26px", cursor: "pointer", position: "relative", display: "flex", alignItems: "center", padding: "3px" }}>
          <div style={{ width: "20px", height: "20px", borderRadius: "50%", background: "#C9A84C", transform: theme === "dark" ? "translateX(22px)" : "translateX(0)", transition: "transform 0.25s", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px" }}>
            {theme === "dark" ? "🌙" : "☀️"}
          </div>
        </button>

        <div style={{ position: "relative" }}>
          <button onClick={() => { setLangOpen(!langOpen); setProfileOpen(false); }} style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(201,168,76,0.4)", color: "#C9A84C", borderRadius: "6px", padding: "5px 10px", fontSize: "13px", cursor: "pointer", display: "flex", alignItems: "center", gap: "5px" }}>
            {LANGUAGES.find(l => l.code === lang)?.label} <span style={{ fontSize: "9px" }}>▼</span>
          </button>
          {langOpen && (
            <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", background: "#2C2C2C", border: "1px solid #C9A84C", borderRadius: "8px", overflow: "hidden", minWidth: "140px", zIndex: 300 }}>
              {LANGUAGES.map(l => (
                <button key={l.code} onClick={() => { setLang(l.code); setLangOpen(false); }}
                  style={{ display: "block", width: "100%", padding: "8px 14px", textAlign: "left", background: l.code === lang ? "rgba(201,168,76,0.15)" : "transparent", color: l.code === lang ? "#C9A84C" : "#D0CCC8", border: "none", cursor: "pointer", fontSize: "13px", borderBottom: "0.5px solid rgba(255,255,255,0.05)" }}>
                  {l.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {user ? (
          <div style={{ position: "relative" }}>
            <button onClick={() => { setProfileOpen(!profileOpen); setLangOpen(false); }}
              style={{ width: "38px", height: "38px", borderRadius: "50%", background: "#C9A84C", border: "2px solid #A07828", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "15px", color: "#2C2C2C", overflow: "hidden", padding: 0 }}>
              {picture ? (
                <img src={picture} alt="Profile" style={{ width: "38px", height: "38px", borderRadius: "50%", objectFit: "cover" }} onError={() => setPicture("")} />
              ) : initials}
            </button>
            {profileOpen && (
              <div style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", background: "var(--white)", border: "1px solid var(--border)", borderRadius: "12px", minWidth: "210px", zIndex: 300, overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}>
                <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)", background: "var(--offwhite)" }}>
                  <div style={{ fontWeight: 600, fontSize: "14px", color: "var(--text-primary)" }}>{user.name || "User"}</div>
                  <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>{user.email}</div>
                  <span style={{ fontSize: "11px", background: "#C9A84C", color: "#2C2C2C", padding: "2px 8px", borderRadius: "10px", fontWeight: 700, display: "inline-block", marginTop: "6px" }}>
                    {(user.plan || "free").toUpperCase()}
                  </span>
                </div>
                {[
                  { label: "Account Settings", path: "/account/settings", icon: "⚙" },
                  { label: "My Reports", path: "/account/reports", icon: "📄" },
                  { label: "Accessibility", path: "/account/accessibility", icon: "♿" },
                  { label: "Plans & Subscriptions", path: "/account/plans", icon: "💳" },
                ].map(item => (
                  <Link key={item.path} to={item.path} onClick={() => setProfileOpen(false)}
                    style={{ display: "flex", alignItems: "center", gap: "10px", padding: "11px 16px", fontSize: "14px", color: "var(--text-primary)", borderBottom: "0.5px solid var(--border)", transition: "background 0.15s", background: "transparent", textDecoration: "none" }}
                    onMouseEnter={e => { e.currentTarget.style.background = "var(--offwhite)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                    <span style={{ fontSize: "14px" }}>{item.icon}</span> {item.label}
                  </Link>
                ))}
                <button onClick={logout} style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", padding: "11px 16px", textAlign: "left", fontSize: "14px", color: "var(--error)", background: "transparent", border: "none", cursor: "pointer", fontWeight: 500 }}>
                  <span>🚪</span> Logout
                </button>
              </div>
            )}
          </div>
        ) : (
          <a href="/api/auth/google" style={{ background: "#C9A84C", color: "#2C2C2C", border: "none", padding: "8px 18px", borderRadius: "8px", fontSize: "13px", fontWeight: 700, cursor: "pointer", textDecoration: "none" }}>
            Login
          </a>
        )}
      </div>
    </nav>
  );
}
